import React, { useState } from 'react';
import Layout from '../../components/layout/Layout';
import Hero from '../../components/sections/Hero';
import Section from '../../components/layout/Section';
import { blogPosts } from '../../data/mockData';

const BlogPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');

  // Extract unique categories from blog posts
  const categoriesSet = new Set(blogPosts.map(post => post.category));
  const categories = Array.from(categoriesSet);
  
  // Filter posts based on search term and category
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = searchTerm === '' || 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = category === '' || post.category === category;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <Layout>
      <Hero
        title="AI Insights Blog"
        subtitle="Expert perspectives, practical guides, and the latest thinking on AI implementation and optimization for businesses."
        backgroundType="gradient"
        ctaText="Browse Articles"
        ctaLink="#articles"
      />

      {/* Search and Filter Section */}
      <Section className="bg-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="w-full md:w-1/2">
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="Search articles..." 
                  className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="absolute left-3 top-3 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="w-full md:w-1/4">
              <select 
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </Section>

      {/* Featured Post */}
      {filteredPosts.length > 0 && (
        <Section className="bg-gray-50 py-12">
          <div className="container mx-auto px-4">
            <div className="bg-white rounded-lg shadow-md overflow-hidden md:flex">
              <div className="md:w-1/2 bg-gray-200 md:h-auto h-64">
                {/* Placeholder for featured post image */}
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="md:w-1/2 p-8">
                <div className="uppercase tracking-wide text-sm text-primary font-semibold mb-1">
                  {filteredPosts[0].category}
                </div>
                <h2 className="text-3xl font-bold mb-2">{filteredPosts[0].title}</h2>
                <p className="text-gray-600 mb-4">{filteredPosts[0].author} • {filteredPosts[0].publishDate} • {filteredPosts[0].readTime}</p>
                <p className="text-gray-700 mb-6">{filteredPosts[0].excerpt}</p>
                <a 
                  href={filteredPosts[0].url} 
                  className="inline-block bg-primary hover:bg-primary-dark text-white font-bold py-2 px-6 rounded transition-colors duration-200"
                >
                  Read Article
                </a>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Blog Posts Grid */}
      <Section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Latest Articles</h2>
          
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-gray-600">No articles found matching your criteria.</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setCategory('');
                }}
                className="mt-4 text-primary hover:underline font-medium"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {filteredPosts.slice(1).map(post => (
                <div key={post.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="h-48 bg-gray-200">
                    {/* Placeholder for post image */}
                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="uppercase tracking-wide text-xs text-primary font-semibold mb-1">
                      {post.category}
                    </div>
                    <h3 className="text-xl font-bold mb-2">{post.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{post.author} • {post.publishDate} • {post.readTime}</p>
                    <p className="text-gray-700 mb-4">{post.excerpt}</p>
                    <a 
                      href={post.url} 
                      className="text-primary hover:text-primary-dark font-medium"
                    >
                      Read More →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination - Simple version */}
          {filteredPosts.length > 0 && (
            <div className="flex justify-center mt-12">
              <nav className="inline-flex rounded-md shadow">
                <a 
                  href="#" 
                  className="py-2 px-4 border border-gray-300 bg-white text-sm font-medium rounded-l-md text-gray-700 hover:bg-gray-50"
                >
                  Previous
                </a>
                <a 
                  href="#" 
                  className="py-2 px-4 border border-gray-300 bg-white text-sm font-medium text-primary-dark"
                >
                  1
                </a>
                <a 
                  href="#" 
                  className="py-2 px-4 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  2
                </a>
                <a 
                  href="#" 
                  className="py-2 px-4 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  3
                </a>
                <a 
                  href="#" 
                  className="py-2 px-4 border border-gray-300 bg-white text-sm font-medium rounded-r-md text-gray-700 hover:bg-gray-50"
                >
                  Next
                </a>
              </nav>
            </div>
          )}
        </div>
      </Section>

      {/* Newsletter Signup */}
      <Section className="bg-primary bg-opacity-5 py-16">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-4">Stay Updated with AI Insights</h2>
          <p className="text-lg mb-8">
            Subscribe to our newsletter for the latest articles, guides, and industry insights delivered directly to your inbox.
          </p>
          <div className="flex flex-col md:flex-row gap-2 justify-center max-w-xl mx-auto">
            <input 
              type="email" 
              placeholder="Your email address" 
              className="flex-grow p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <button className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-6 rounded transition-colors duration-200">
              Subscribe
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            We respect your privacy. You can unsubscribe at any time.
          </p>
        </div>
      </Section>
    </Layout>
  );
};

export default BlogPage;